import { batch, useComputed } from "@suse-o11y/signal";
import React from "react";

import { Editor } from "~/components/code/editor";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { Select } from "~/components/select";
import { SelectContent } from "~/components/select/content";
import { SelectItem } from "~/components/select/item";
import { SelectTrigger } from "~/components/select/trigger";
import { SelectValue } from "~/components/select/value";
import { cx } from "~/tools/class-variance";
import { onIdle } from "~/tools/on-idle";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";

import type { DashboardPluginSpecKind } from "../../common/dashboard-spec";

import type { FormState } from "./_form-state";

const MARKDOWN_TYPE = "Markdown" satisfies DashboardPluginSpecKind;

const DASHBOARD_PLUGIN_KINDS = [
	"TimeSeriesChart",
	"BarChart",
	"StatChart",
	"GaugeChart",
	"Markdown",
] as const satisfies ReadonlyArray<DashboardPluginSpecKind>;

function useFocusInput() {
	const editor = React.useRef<React.ComponentRef<typeof Editor>>(null);

	React.useEffect(() => {
		// the drawer does not allow to autoFocus the input
		// neither .focus() during synchronous effect
		return onIdle(() => {
			editor.current?.focus();
		});
	}, []);

	return editor;
}

export interface GenericFieldsProps {
	form: FormState;
	className?: string;
}

export const GenericFields: React.FC<GenericFieldsProps> = ({ form, className }) => {
	const nameInputRef = useFocusInput();
	const variables = useTemplateVariables();

	const isMarkdownKind = useComputed(
		(monitor) => form.fields.kind.getActiveBranch(monitor)?.kind === "markdown",
		[form],
	);

	return (
		<div className={cx("space-y-4", className)}>
			<FormField state={form.fields.name}>
				<FormLabel asChild>
					<div>
						<FormLabel>Name *</FormLabel>
						<FormFieldReset />
					</div>
				</FormLabel>

				{(monitor, field) => (
					<Editor
						appearance="input"
						language={{ name: "template", variables: variables.suggestions }}
						onChange={(name) => field.setInput(name)}
						ref={nameInputRef}
						value={field.getInput(monitor)}
					/>
				)}
			</FormField>

			<FormField state={form.fields.description}>
				<FormLabel asChild>
					<div>
						<FormLabel>Description</FormLabel>
						<FormFieldReset />
					</div>
				</FormLabel>

				{(monitor, field) => (
					<Editor
						appearance="textarea"
						height={{ maxLines: 5, minLines: 5 }}
						language={{ name: "template", variables: variables.suggestions }}
						onChange={(description) => field.setInput(description)}
						value={field.getInput(monitor)}
					/>
				)}
			</FormField>

			<FormField
				state={isMarkdownKind ? form.fields.kind.active : form.fields.kind.branches.metrics.fields.chart.active}
			>
				<FormLabel asChild>
					<div>
						<FormLabel>Type</FormLabel>
						<FormFieldReset state={[form.fields.kind.active, form.fields.kind.branches.metrics.fields.chart.active]} />
					</div>
				</FormLabel>

				{(monitor) => (
					<Select
						name="dashboard-widget-type"
						onValueChange={(input) => {
							if (input === MARKDOWN_TYPE) {
								form.fields.kind.active.setInput("markdown");
							} else {
								batch(() => {
									form.fields.kind.active.setInput("metrics");
									form.fields.kind.branches.metrics.fields.chart.active.setInput(input);
								});
							}
						}}
						value={
							isMarkdownKind ? MARKDOWN_TYPE : form.fields.kind.branches.metrics.fields.chart.active.getInput(monitor)
						}
					>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>

						<SelectContent>
							{DASHBOARD_PLUGIN_KINDS.map((pluginKind) => (
								<SelectItem key={pluginKind} value={pluginKind}>
									{pluginKind}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</FormField>
		</div>
	);
};
